import React, { useEffect, useState } from "react";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  makeStyles,
  Typography,
  Box,
  Chip,
  Tooltip,
  Button,
  Card,
  CardContent,
  Divider,
  Select,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import ConfirmationModal from "../ConfirmationModal";
import RegisterCompanyImproved from "./RegisterCompanyImproved";

import { 
  Edit as EditIcon, 
  Save as SaveIcon, 
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Add as AddIcon
} from "@material-ui/icons";

import { has, head, isArray } from "lodash";
import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import { useDate } from "../../hooks/useDate";
import { useTimezone } from "../../hooks/useTimezone";
import usePlans from "../../hooks/usePlans";
import { getTimezone } from "../../services/settings.api";
import ModalUsers from "../ModalUsers";
import { i18n } from "../../translate/i18n";
import moment from "moment";
import { useStyles } from "./CompaniesManagerImprovedStyles";
import CompaniesManagerGridImproved from "./CompaniesManagerGridImproved";
import CompanyFormImproved from "./CompanyFormImproved";

/** Estado inicial del formulario edición (sin id) */
const EMPTY_COMPANY_RECORD = {
  name: "",
  email: "",
  phone: "",
  planId: "",
  status: true,
  campaignsEnabled: false,
  dueDate: "",
  recurrence: "",
  timezone: "",
};


export default function CompaniesManagerImproved() {
  const classes = useStyles();
  const { list, save, update, remove, find } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({ ...EMPTY_COMPANY_RECORD });
  const [openEditDialog, setOpenEditDialog] = useState(false);

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      setRecords(companyList);
    } catch (e) {
      toast.error(i18n.t("companies.messages.loadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      let savedCompany;
      if (data.id !== undefined) {
        savedCompany = await update(data);
      } else {
        savedCompany = await save(data);
      }

      await loadPlans();

      // ✅ Refrescar fila + formulario con datos del servidor (misma ruta que al elegir la empresa en la tabla)
      const companyId = savedCompany?.id || data.id;
      if (companyId) {
        try {
          const full = await find(companyId);
          await handleSelect(full);
        } catch (err) {
          console.warn("[CompaniesManager] find tras guardar falló, usando respuesta del PUT", err);
          if (savedCompany && savedCompany.id) {
            await handleSelect(savedCompany);
          }
        }
      }

      if (!data.id) {
        handleCancel();
      }

      toast.success(i18n.t("companies.messages.saveSuccess"));
    } catch (e) {
      toast.error(i18n.t("companies.messages.saveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCloseEditModal();
      toast.success(i18n.t("companies.messages.deleteSuccess"));
    } catch (e) {
      toast.error(i18n.t("companies.messages.deleteError"));
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  /** Cierra el modal de edición y limpia la empresa seleccionada */
  const handleCloseEditModal = () => {
    setOpenEditDialog(false);
    setRecord({ ...EMPTY_COMPANY_RECORD });
  };

  /** Limpiar campos del formulario (botón Limpiar), sin cerrar el modal */
  const handleCancel = () => {
    setRecord((prev) => ({
      ...prev,
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      campaignsEnabled: false,
      dueDate: "",
      recurrence: "",
    }));
  };

  const handleSelect = async (data) => {
    setOpenRegisterDialog(false);

    let campaignsEnabled = false;

    const settingsList = Array.isArray(data.settings) ? data.settings : [];
    const setting = settingsList.find(
      (s) => s.key.indexOf("campaignsEnabled") > -1
    );
    if (setting) {
      campaignsEnabled = setting.value === "true" || setting.value === "enabled";
    }

    // ✅ FORMATEAR FECHA DE VENCIMIENTO CORRECTAMENTE
    let formattedDueDate = "";
    if (data.dueDate) {
      const momentDate = moment(data.dueDate);
      if (momentDate.isValid()) {
        formattedDueDate = momentDate.format("YYYY-MM-DD");
      }
    }

    // ✅ DEBUG: Verificar qué datos vienen de la empresa
    console.log(`[handleSelect] Datos recibidos para empresa ${data.id}:`, {
      id: data.id,
      name: data.name,
      timezone: data.timezone,
      hasSettings: !!data.settings,
      settingsCount: data.settings?.length || 0
    });

    // ✅ CARGAR TIMEZONE DESDE SETTINGS
    // ✅ PRIORIDAD: Settings.timezone > Companies.timezone > Default
    let timezone = "America/Lima"; // Default
    
    // ✅ PRIMERO: Intentar usar timezone de Companies si está disponible
    if (data.timezone) {
      timezone = data.timezone;
      console.log(`[handleSelect] Timezone inicial desde Companies: ${data.timezone}`);
    }
    
    // ✅ SEGUNDO: Intentar cargar desde Settings (sobrescribe si existe)
    if (data.id) {
      try {
        const { data: timezoneData } = await getTimezone({
          params: { companyId: data.id }
        });
        if (timezoneData && timezoneData.value) {
          timezone = timezoneData.value;
          console.log(`[handleSelect] Timezone cargado desde Settings: ${timezoneData.value}`);
        } else {
          // Settings no tiene valor, mantener el de Companies si existe
          if (data.timezone) {
            console.log(`[handleSelect] Settings vacío, usando timezone de Companies: ${data.timezone}`);
          }
        }
      } catch (error) {
        // ✅ 404 es esperado si Settings no tiene timezone configurado aún
        // ✅ NO mostrar warning para 404, solo para otros errores
        if (error.response?.status === 404) {
          // 404 esperado, usar timezone de Companies si existe
          if (data.timezone) {
            console.log(`[handleSelect] Settings no configurado (404), usando timezone de Companies: ${data.timezone}`);
          } else {
            console.log(`[handleSelect] Settings no configurado (404) y Companies sin timezone, usando default: America/Lima`);
          }
        } else {
          // Error diferente a 404, mostrar warning
          console.warn(`[handleSelect] Error cargando timezone desde Settings (no es 404):`, error);
          // Mantener timezone de Companies si existe
          if (!data.timezone) {
            console.warn(`[handleSelect] No hay timezone en Companies ni Settings, usando default`);
          }
        }
      }
    } else if (data.timezone) {
      // ✅ Si es nueva empresa pero tiene timezone en data, usarlo
      timezone = data.timezone;
      console.log(`[handleSelect] Nueva empresa con timezone: ${data.timezone}`);
    }
    
    console.log(`[handleSelect] Timezone final seleccionado: ${timezone}`);

    const newRecord = {
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId ? String(data.planId) : "", // ✅ Convertir a string para evitar warning
      status: data.status === false ? false : true,
      campaignsEnabled,
      dueDate: formattedDueDate,
      recurrence: data.recurrence || "",
      timezone: timezone, // ✅ Agregar timezone al record
    };
    
    // ✅ Asegurar que planId sea string también en el record actualizado
    if (newRecord.planId && typeof newRecord.planId !== 'string') {
      newRecord.planId = String(newRecord.planId);
    }

    setRecord((prev) => ({
      ...prev,
      ...newRecord,
    }));
    setOpenEditDialog(true);
  };

  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);

  const handleOpenRegisterDialog = () => {
    handleCloseEditModal();
    setOpenRegisterDialog(true);
  };

  const handleCloseRegisterDialog = () => {
    setOpenRegisterDialog(false);
  };

  const handleRegisterSubmit = async (companyData) => {
    // ✅ MEJORADO: El callback ya recibe la empresa creada del backend
    // Solo necesitamos actualizar la lista y cerrar el modal
    try {
      // ✅ Actualizar la lista de empresas para mostrar la nueva
      await loadPlans();
      // ✅ Cerrar el modal
      handleCloseRegisterDialog();
      // ✅ El mensaje de éxito ya se mostró en RegisterCompanyImproved
      // No duplicar el mensaje, solo confirmar
      toast.success(i18n.t("companies.messages.saveSuccess"));
    } catch (e) {
      console.error("Error actualizando lista de empresas:", e);
      // ✅ Aún así cerrar el modal y mostrar mensaje de éxito
      handleCloseRegisterDialog();
      toast.success(i18n.t("companies.messages.registerRefreshPage"));
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={openEditDialog && !!record.id}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          style: {
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogContent
          style={{
            padding: 0,
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {record.id ? (
            <CompanyFormImproved
              initialValue={record}
              onDelete={handleOpenDeleteDialog}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" style={{ fontWeight: "bold" }}>
          {i18n.t("companies.title")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenRegisterDialog}
          className={classes.primaryButton}
        >
          {i18n.t("companies.buttons.registerCompany")}
        </Button>
      </Box>
      
      <CompaniesManagerGridImproved 
        records={records} 
        onSelect={handleSelect} 
      />
      
      <Dialog
        open={openRegisterDialog}
        onClose={handleCloseRegisterDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <DialogContent style={{ 
          padding: 0, 
          overflowY: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <RegisterCompanyImproved
            onSuccess={handleRegisterSubmit}
            onCancel={handleCloseRegisterDialog}
          />
        </DialogContent>
      </Dialog>
      
      <ConfirmationModal
        title={i18n.t("companies.messages.deleteTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {i18n.t("companies.messages.deleteMessage")}
      </ConfirmationModal>
    </div>
  );
}
